import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom'
import { CustomerFormContainer } from '@/modules/customer/containers/customer-form.container'
import { CustomerProfileContainer } from '@/modules/customer/containers/customer-profile.container'
import { CustomerSearchContainer } from '@/modules/customer/containers/customer-search.container'
import { DocListContainer } from '@/modules/docs/containers/doc-list.container'
import { DocContainer } from '@/modules/docs/containers/doc.container'
import { ReleaseNotesContainer } from '@/modules/docs/containers/release-notes.container'
import { HomeContainer } from '@/modules/home/containers/home.container'
import { Report } from '@/modules/report'
import { ReportSalesByClient } from '@/modules/report/components/sales-by-client'
import { ReportSalesByProduct } from '@/modules/report/components/sales-by-product'
import { ManageSellersContainer } from '@/modules/report/containers/manage-sellers.container'
import { NearbyCustomersContainer } from '@/modules/report/containers/nearby-customers.container'
import { SalesComissionContainer } from '@/modules/report/containers/sales-commission.container'
import { SalesGoalContainer } from '@/modules/report/containers/sales-goals.container'
import { ScheduleFormContainer } from '@/modules/schedule/containers/schedule-form.container'
import { ScheduleListContainer } from '@/modules/schedule/containers/schedule-list.container'
import { ErrorPage } from '@/modules/shared/components/error-page'
import { STORAGE_KEYS } from '@/modules/shared/constants/storage-keys'
import { api } from '@/modules/shared/lib/axios'
import {
	getItemFromStorage,
	setItemInStorage,
} from '@/modules/shared/utils/storage-helper'

import PackageJson from '../../../../package.json'
import { useAuthStore } from '../stores/auth.store'
import { PromotionSearchContainer } from '@/modules/promotion/container/promotion-search.container'
import { PromotionVisitFormContainer } from '@/modules/promotion/container/promotion-visit-form.container'
import { RouteClientsContainer } from '@/modules/routes/containers/routes.container'
import { RouterErrorWrapper } from '../components/error-boundary/router-error-wrapper'
import { AuthChangePasswordPage, AuthEmailPage, AuthForgetPasswordPage, AuthPasswordPage, AuthRegisterPage } from '../../auth/pages'
import { OrderFormPage } from '../../order/pages/order-form.page'
import { OrderSearchPage } from '../../order/pages/order-search.page'
import { OrderTransmissionPage } from '../../order/pages/order-transmission.page'
import { ComparativeSellersContainer } from '@/modules/report/containers/comparative-sellers.container'

const Protected = () => {
	const {
		token,
		actions: { setToken, setVersionUrl, setUser },
	} = useAuthStore()

	const params = new URLSearchParams(location.search)
	const tokenQuery = params.get('token')
	const userData = params.get('user')
	const versionUrl = params.get('versionUrl')

	if (tokenQuery && userData && versionUrl) {
		api.defaults.baseURL = versionUrl
		setVersionUrl(versionUrl)
		setItemInStorage('version-app', PackageJson.version)
		const userDataParsed = JSON.parse(userData)
		setUser({
			id: userDataParsed.id,
			vendedor: userDataParsed.vendedor,
			vendedorEmail: userDataParsed.vendedorEmail,
			empresaNome: userDataParsed.empresaNome,
			nome: userDataParsed.nome,
		})

		setToken(tokenQuery)

		history.replaceState({}, document.title, location.pathname)
	}

	if (!token) {
		return <Navigate to="/login" />
	}

	return <Outlet />
}

const Public = () => {
	const token = getItemFromStorage(STORAGE_KEYS.TOKEN)

	if (token) {
		return <Navigate to="/" />
	}

	return <Outlet />
}

export const router = createBrowserRouter([
	{
		path: '/',
		element: <Protected />,
		errorElement: <RouterErrorWrapper />,
		children: [
			{
				path: '/',
				element: <HomeContainer />,
			},
			{
				path: '/transmitir',
				element: <OrderTransmissionPage />,
			},
			{
				path: '/pedidos',
				element: <OrderSearchPage />,
			},
			{
				path: '/pedido/add',
				element: <OrderFormPage />,
			},
			{
				path: '/pedido/edit/:id',
				element: <OrderFormPage />,
			},
			{
				path: '/pedido/view/:id',
				element: <OrderFormPage />,
			},
			{
				path: '/clientes',
				element: <CustomerSearchContainer />,
			},
			{
				path: '/visitas',
				element: <PromotionSearchContainer />,
			},
			{
				path: '/visitas/novo',
				element: <PromotionVisitFormContainer />,
			},
			{
				path: '/visitas/:id/editar',
				element: <PromotionVisitFormContainer />,
			},
			{
				path: '/visitas/:id/visualizar',
				element: <PromotionVisitFormContainer />,
			},
			{
				path: '/cliente/add',
				element: <CustomerFormContainer />,
			},
			{
				path: '/rotas',
				element: <RouteClientsContainer />,
			},
			{
				path: '/cliente/edit/:id',
				element: <CustomerFormContainer />,
			},
			{
				path: '/cliente/view/:id',
				element: <CustomerFormContainer />,
			},
			{
				path: '/cliente/profile/:id',
				element: <CustomerProfileContainer />,
			},
			{
				path: '/relatorios',
				element: <Report />,
			},
			{
				path: '/relatorios/meta',
				element: <SalesGoalContainer />,
			},
			{
				path: '/relatorios/comissao',
				element: <SalesComissionContainer />,
			},
			{
				path: '/relatorios/vendas_cliente',
				element: <ReportSalesByClient />,
			},
			{
				path: '/relatorios/vendas_produto',
				element: <ReportSalesByProduct />,
			},
			{
				path: '/clientes-proximos',
				element: <NearbyCustomersContainer />,
			},
			{
				path: '/atualizacoes',
				element: <ReleaseNotesContainer />,
			},
			{
				path: '/documentacao/:slug',
				element: <DocContainer />,
			},
			{
				path: '/documentacao',
				element: <DocListContainer />,
			},

			{
				path: '/relatorios/gestao',
				element: <ManageSellersContainer />,
			},

			{
				path: '/relatorios/comparativo',
				element: <ComparativeSellersContainer />,
			},

			{
				path: '/agendamentos',
				element: <ScheduleListContainer />,
			},
			{
				path: '/agendamento/editar/:id',
				element: <ScheduleFormContainer />,
			},
			{
				path: '/agendamento/adicionar',
				element: <ScheduleFormContainer />,
			},
		],
	},
	{
		path: '/',
		element: <Public />,
		errorElement: <RouterErrorWrapper />,
		children: [
			{
				path: '/login',
				element: <AuthEmailPage />,
			},
			{
				path: '/confirmar-senha',
				element: <AuthPasswordPage />,
			},
			{
				path: '/cadastro',
				element: <AuthRegisterPage />,
			},
			{
				path: '/esqueci-minha-senha',
				element: <AuthForgetPasswordPage />,
			},
			{
				path: '/alterar-senha/:id',
				element: <AuthChangePasswordPage />,
			},
			{
				path: '*',
				element: <ErrorPage />,
			},
		],
	},
])
